
import React from 'react';
import { Card } from "@/components/ui/card";
import { Brain, ArrowUpRight, Clock, TrendingUp, AlertCircle } from 'lucide-react';
import { getTimeOfDayInsights, getConfidenceAccuracyGaps, getLearningHabits, getImprovementAreas } from './utils/insightAnalysis';
import { StreakTracker } from './StreakTracker';
import { ScoreDistribution } from './ScoreDistribution';
import { useIsMobile } from '@/hooks/use-mobile';
import type { PerformanceData, StudyHabit } from './types';

interface AIInsightsCardsProps {
  performanceData: PerformanceData[];
}

export const AIInsightsCards: React.FC<AIInsightsCardsProps> = ({ performanceData }) => {
  const { isMobile, screenSize } = useIsMobile();
  
  const timeInsights = React.useMemo(() => 
    getTimeOfDayInsights(performanceData),
    [performanceData]
  );

  const confidenceGaps = React.useMemo(() => 
    getConfidenceAccuracyGaps(performanceData),
    [performanceData]
  );

  const habits = React.useMemo(() => 
    getLearningHabits(performanceData),
    [performanceData]
  );

  const improvement = React.useMemo(() => 
    getImprovementAreas(performanceData),
    [performanceData]
  );

  if (performanceData.length < 5) {
    return (
      <Card className={isMobile ? "p-4" : "p-6"}>
        <div className="flex items-center gap-2 mb-4">
          <Brain className={`${isMobile ? "w-5 h-5" : "w-6 h-6"} text-purple-500`} />
          <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-semibold`}>AI Learning Insights</h2>
        </div>
        <p className={`${isMobile ? "text-sm" : ""} text-gray-500`}>Complete more quizzes to receive personalized insights</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
        {timeInsights.timeComparison && (
          <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-purple-50 to-white hover:shadow-lg transition-shadow duration-300`}>
            <div className="flex justify-between items-start mb-2 sm:mb-4">
              <div className="flex items-center gap-1 sm:gap-2">
                <Clock className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-purple-600`} />
                <h3 className={`${isMobile ? "text-sm" : "text-lg"} font-semibold`}>Best Study Time</h3>
              </div>
              <span className={`bg-purple-100 text-purple-700 ${isMobile ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"} rounded-full`}>
                New Insight
              </span>
            </div>
            <p className={`${isMobile ? "text-xs" : ""} text-gray-700 mb-2 sm:mb-4`}>{timeInsights.timeComparison}</p>
            <a href="#" className={`${isMobile ? "text-[10px]" : "text-sm"} text-purple-600 font-medium flex items-center hover:text-purple-800`}>
              View time analysis
              <ArrowUpRight className={`${isMobile ? "w-2.5 h-2.5" : "w-4 h-4"} ml-1`} />
            </a>
          </Card>
        )}
        
        {habits.length > 0 && (
          <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-blue-50 to-white hover:shadow-lg transition-shadow duration-300`}>
            <div className="flex justify-between items-start mb-2 sm:mb-4">
              <div className="flex items-center gap-1 sm:gap-2">
                <TrendingUp className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-blue-600`} />
                <h3 className={`${isMobile ? "text-sm" : "text-lg"} font-semibold`}>Learning Habit</h3>
              </div>
              {habits[0].status === 'good' ? (
                <span className={`bg-green-100 text-green-700 ${isMobile ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"} rounded-full`}>
                  Good
                </span>
              ) : (
                <span className={`bg-yellow-100 text-yellow-700 ${isMobile ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"} rounded-full`}>
                  Needs Work
                </span>
              )}
            </div>
            <p className={`${isMobile ? "text-xs" : ""} text-gray-700 mb-2 sm:mb-4`}>{habits[0].description}</p>
            <a href="#" className={`${isMobile ? "text-[10px]" : "text-sm"} text-blue-600 font-medium flex items-center hover:text-blue-800`}>
              View all habits
              <ArrowUpRight className={`${isMobile ? "w-2.5 h-2.5" : "w-4 h-4"} ml-1`} />
            </a>
          </Card>
        )}
        
        {improvement.length > 0 && (
          <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-amber-50 to-white hover:shadow-lg transition-shadow duration-300`}>
            <div className="flex justify-between items-start mb-2 sm:mb-4">
              <div className="flex items-center gap-1 sm:gap-2">
                <AlertCircle className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-amber-600`} />
                <h3 className={`${isMobile ? "text-sm" : "text-lg"} font-semibold`}>Focus Area</h3>
              </div>
              <span className={`bg-${improvement[0].trend === 'declining' ? 'red' : 'amber'}-100 
                text-${improvement[0].trend === 'declining' ? 'red' : 'amber'}-700 
                ${isMobile ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"} rounded-full`}>
                {improvement[0].trend === 'declining' ? 'Priority' : 'Recommended'}
              </span>
            </div>
            <p className={`${isMobile ? "text-xs" : ""} text-gray-700 mb-2 sm:mb-4`}>{improvement[0].message}</p>
            <a href="#" className={`${isMobile ? "text-[10px]" : "text-sm"} text-amber-600 font-medium flex items-center hover:text-amber-800`}>
              View subject analysis
              <ArrowUpRight className={`${isMobile ? "w-2.5 h-2.5" : "w-4 h-4"} ml-1`} />
            </a>
          </Card>
        )}
      </div>
      
      {confidenceGaps.length > 0 && (
        <Card className={`${isMobile ? "p-3" : "p-6"} bg-gradient-to-br from-indigo-50 to-white hover:shadow-lg transition-shadow duration-300`}>
          <div className="flex justify-between items-center mb-2 sm:mb-4">
            <div className="flex items-center gap-1 sm:gap-2">
              <Brain className={`${isMobile ? "w-4 h-4" : "w-5 h-5"} text-indigo-600`} />
              <h3 className={`${isMobile ? "text-sm" : "text-lg"} font-semibold`}>Confidence vs. Accuracy Gaps</h3>
            </div>
          </div>
          <div className="space-y-2">
            {confidenceGaps.slice(0, 2).map((gap, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className={`${isMobile ? "text-xs" : ""} font-medium truncate`}>{gap.subject}</p>
                  <div className={`flex items-center gap-2 sm:gap-4 ${isMobile ? "text-[10px]" : "text-sm"} text-gray-500`}>
                    <span>Conf: {Math.round(gap.confidence)}%</span>
                    <span>Acc: {Math.round(gap.accuracy)}%</span>
                  </div>
                </div>
                <span className={`${isMobile ? "text-xs" : ""} text-red-500 font-medium`}>{Math.round(gap.gap)}% gap</span>
              </div>
            ))}
          </div>
        </Card>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6">
        <StreakTracker performanceData={performanceData} />
        <ScoreDistribution performanceData={performanceData} />
      </div>
    </div>
  );
};
